import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Maximize2, Minus, Plus } from 'lucide-react';
import {
  VALUE_CHAIN_MAX_ZOOM,
  VALUE_CHAIN_MIN_ZOOM,
  ValueChainCanvas,
} from '@/components/value-chain/ValueChainCanvas';
import { ValueChainDetailsPanel } from '@/components/value-chain/ValueChainDetailsPanel';
import { layoutValueChainTree } from '@/components/value-chain/layoutTree';
import { neighborhoodIds } from '@/components/value-chain/treeIndex';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import type {
  ValueChainDetailsTab,
  ValueChainGraph,
  ValueChainNode,
} from '@/types/valueChain';

type Props = {
  graphs: ValueChainGraph[];
  graph: ValueChainGraph;
  index: TreeIndex;
  selectedId: string;
  selected: ValueChainNode | null;
  onSelect: (id: string) => void;
  onGraphChange: (id: string) => void;
  detailsTab: ValueChainDetailsTab;
  onDetailsTabChange: (tab: ValueChainDetailsTab) => void;
  note: string;
  onNoteChange: (value: string) => void;
  editing: boolean;
  draftDescription: string;
  onToggleEdit: () => void;
  onDraftChange: (value: string) => void;
  onSaveEdit: () => void;
  onAddRelation: () => void;
  focused: boolean;
  onOpenFullGraph: () => void;
};

export function GraphWorkspace({
  graphs,
  graph,
  index,
  selectedId,
  selected,
  onSelect,
  onGraphChange,
  detailsTab,
  onDetailsTabChange,
  note,
  onNoteChange,
  editing,
  draftDescription,
  onToggleEdit,
  onDraftChange,
  onSaveEdit,
  onAddRelation,
  focused,
  onOpenFullGraph,
}: Props) {
  const canvasRef = useRef<HTMLElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showGraphMenu, setShowGraphMenu] = useState(false);
  const [showSupplyChain, setShowSupplyChain] = useState(focused);

  const visibleIds = useMemo(() => {
    if (focused) {
      const nearby = neighborhoodIds(index, selectedId, 2);
      if (showSupplyChain) return nearby;
      return new Set(
        [...nearby].filter((id) => index.byId.get(id)?.type !== 'Company'),
      );
    }
    return new Set(
      graph.nodes
        .filter((node) => showSupplyChain || node.type !== 'Company')
        .map((node) => node.id),
    );
  }, [focused, graph.nodes, index, selectedId, showSupplyChain]);

  const visibleNodes = useMemo(
    () => graph.nodes.filter((node) => visibleIds.has(node.id)),
    [graph.nodes, visibleIds],
  );

  const layoutRoot = focused
    ? index.parent.get(selectedId) && visibleIds.has(index.parent.get(selectedId) ?? '')
      ? index.parent.get(selectedId) ?? graph.rootId
      : selectedId
    : graph.rootId;

  const layout = useMemo(
    () => layoutValueChainTree(layoutRoot, graph.nodes, graph.edges, visibleIds, showSupplyChain),
    [graph.edges, graph.nodes, layoutRoot, showSupplyChain, visibleIds],
  );

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || layout.width <= 0 || layout.height <= 0) return;
    const nextZoom = Math.min(
      1,
      (el.clientWidth - 48) / layout.width,
      (el.clientHeight - 96) / layout.height,
    );
    setZoom(Math.max(focused ? 0.55 : 0.25, Number.isFinite(nextZoom) ? nextZoom : 1));
    setPan({ x: 0, y: 0 });
  }, [focused, graph.id, layout.height, layout.width, showSupplyChain, layoutRoot]);

  const outgoing = graph.edges.filter(
    (edge) => edge.from === selectedId && (!edge.supplyChain || showSupplyChain),
  );
  const incoming = graph.edges.filter(
    (edge) => edge.to === selectedId && (!edge.supplyChain || showSupplyChain),
  );
  const parentEdge = incoming.find((edge) => !edge.supplyChain);
  const parent = parentEdge ? graph.nodes.find((node) => node.id === parentEdge.from) : null;
  const relationRows = outgoing
    .map((edge) => {
      const target = graph.nodes.find((node) => node.id === edge.to);
      if (!target) return null;
      return { id: edge.id, relation: edge.relation, target };
    })
    .filter((row): row is NonNullable<typeof row> => Boolean(row));
  const incomingRows = incoming
    .map((edge) => {
      const target = graph.nodes.find((node) => node.id === edge.from);
      if (!target) return null;
      return { id: `${edge.id}-in`, relation: edge.relation, target };
    })
    .filter((row): row is NonNullable<typeof row> => Boolean(row));

  return (
    <div className="flex min-h-0 flex-1">
      <section ref={canvasRef} className="relative min-w-0 flex-1 overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-wrap items-center justify-between gap-2 px-5 py-4">
          <div className="pointer-events-auto relative">
            <button
              type="button"
              onClick={() => setShowGraphMenu((open) => !open)}
              className="inline-flex items-center gap-1 text-[15px] font-medium text-gray-900"
            >
              {graph.name}
              {graph.status ? (
                <span className="ml-2 text-[11px] font-normal text-gray-400">{graph.status}</span>
              ) : null}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {showGraphMenu ? (
              <div className="absolute top-8 left-0 z-20 w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-sm">
                {graphs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      onGraphChange(item.id);
                      setShowGraphMenu(false);
                    }}
                    className={`block w-full px-3 py-2 text-left text-sm ${
                      item.id === graph.id
                        ? 'bg-gray-50 font-medium text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            ) : null}
            <p className="mt-1 text-[12px] text-gray-400">
              {focused
                ? `Neighborhood around the selected node (${visibleNodes.length} nodes)`
                : `Full compiled graph (${visibleNodes.length} nodes)`}
              {focused ? (
                <button
                  type="button"
                  onClick={onOpenFullGraph}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  Open full graph
                </button>
              ) : null}
            </p>
          </div>

          <div className="pointer-events-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSupplyChain((value) => !value)}
              className={`rounded-lg border px-2.5 py-1.5 text-[12px] ${
                showSupplyChain
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600'
              }`}
            >
              Supply chain
            </button>
            <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setZoom((value) => Math.min(VALUE_CHAIN_MAX_ZOOM, value + 0.1))}
                className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-50"
                aria-label="Zoom in"
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setZoom((value) => Math.max(VALUE_CHAIN_MIN_ZOOM, value - 0.1))}
                className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-50"
                aria-label="Zoom out"
              >
                <Minus className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setZoom(1);
                  setPan({ x: 0, y: 0 });
                }}
                className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-50"
                aria-label="Reset view"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <ValueChainCanvas
          nodes={visibleNodes}
          positions={layout.positions}
          edges={layout.edges}
          width={layout.width}
          height={layout.height}
          pan={pan}
          zoom={zoom}
          onSelect={onSelect}
          onPanChange={setPan}
          onZoomChange={setZoom}
        />
      </section>

      <ValueChainDetailsPanel
        node={selected}
        tab={detailsTab}
        onTabChange={onDetailsTabChange}
        parent={parent}
        childrenCount={outgoing.filter((edge) => !edge.supplyChain).length}
        relations={relationRows}
        incoming={incomingRows}
        note={note}
        onNoteChange={onNoteChange}
        editing={editing}
        draftDescription={draftDescription}
        onToggleEdit={onToggleEdit}
        onDraftChange={onDraftChange}
        onSaveEdit={onSaveEdit}
        onAddRelation={onAddRelation}
      />
    </div>
  );
}
